import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProject } from './useProject';

interface JotFormSubmission {
  [key: string]: any;
}

interface UseJotFormDataProps {
  formId?: string;
  limit?: number;
  offset?: number;
}

// Función para generar datos simulados del formulario jurídico
const generateMockJuridicoData = () => {
  const mockSubmissions = [];

  // Datos basados en el PDF analizado
  const areas = ['SER', 'SER', 'SER', 'SER', 'SER', 'Federados (Ramas)', 'Federados (Ramas)', 'Federados (Ramas)', 'er Altius (RCSA)', 'er Altius (RCSA)', 'Universidades (RUA)', 'Universidades (RUA)', 'ORS', 'ORS', 'Otros', 'Otros', 'Otros'];
  const roles = ['Director', 'Director', 'Director', 'Director', 'Director', 'Director', 'Director', 'Director', 'Gerente', 'Gerente', 'Gerente', 'Gerente', 'Gerente', 'Colaborador', 'Colaborador', 'Colaborador', 'Director'];
  const generos = ['Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Hombre', 'Mujer', 'Mujer', 'Mujer'];
  const experienciaGeneral = [9, 4, 5, 6, 8, 7, 10, 9, 4, 5, 6, 8, 9, 4, 5, 6, 8];
  const tiempoRespuesta = [5, 2, 3, 9, 5, 2, 3, 9, 5, 2, 3, 9, 4, 6, 7, 5, 2];
  const recomendacion = [6, 4, 5, 8, 10, 1, 6, 4, 5, 8, 10, 6, 4, 5, 8, 6, 4];

  for (let i = 0; i < 17; i++) {
    mockSubmissions.push({
      id: `submission_${i + 1}`,
      answers: {
        'q1_area': {
          name: 'A que área perteneces:',
          answer: areas[i]
        },
        'q2_rol': {
          name: 'Rol:',
          answer: roles[i]
        },
        'q3_genero': {
          name: 'Sexo:',
          answer: generos[i]
        },
        'q4_experiencia': {
          name: '¿Cómo calificaría su experiencia general con nuestra asesoría jurídica? (1 muy insatisfecho - 10 muy satisfecho)',
          answer: experienciaGeneral[i].toString()
        },
        'q5_tiempo': {
          name: '¿Está satisfecho con el tiempo que tardamos en responder a sus consultas? (1 muy insatisfecho - 10 muy satisfecho)',
          answer: tiempoRespuesta[i].toString()
        },
        'q6_recomendacion': {
          name: '¿Con qué probabilidad recomendarías este servicio jurídico a algún área/persona que necesitase consejo jurídico? (1 muy insatisfecho - 10 muy satisfecho)',
          answer: recomendacion[i].toString()
        },
        'q7_valor_agregado': {
          name: '¿Cuál es el valor agregado que está aportando esta asesoría jurídica?',
          answer: i % 3 === 0 ? 'Da la visión jurídica, permite definir lo "posible" dentro del marco normativo y legal.' :
                  i % 3 === 1 ? 'Dar seguridad jurídica a todas las operaciones y actos de la organización.' :
                  'Un importante apoyo y asesorías para tener certidumbre jurídica.'
        },
        'q8_que_mas_valora': {
          name: '¿Qué es lo que más valora de este servicio jurídico?',
          answer: i % 4 === 0 ? 'La visión y perspectiva sobre la interpretación de las normas y de la ley.' :
                  i % 4 === 1 ? 'La precisión y las opciones que da como resultado.' :
                  i % 4 === 2 ? 'Su oportuno apoyo y orientación.' :
                  'El respaldo y la certeza legal que se requiere.'
        },
        'q9_informacion_clara': {
          name: '¿Considera que fue clara y comprensible la información proporcionada?',
          answer: i < 10 ? 'Sí' : 'No'
        },
        'q10_problema_resuelto': {
          name: '¿Su problema o consulta fue resuelto de manera efectiva?',
          answer: i < 6 ? 'Sí' : 'No'
        },
        // Pregunta de valoración de servicios por áreas (matriz de valoración)
        'q11_servicio_areas': {
          name: 'Valora del 1 al 10 el servicio de las áreas con la/s que tiene trato habitual:',
          answer: (Math.floor(Math.random() * 4) + 7).toString() // Convertir a string y valores entre 7-10
        }
      },
      created_at: new Date(2025, 8, Math.floor(Math.random() * 30) + 1).toISOString()
    });
  }

  return mockSubmissions;
};

export const useJotFormData = ({ formId, limit = 100, offset = 0 }: UseJotFormDataProps = {}) => {
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>(formId);
  const { projectId } = useProject();

  // Función helper para llamar a la edge function
  const callJotFormAPI = async (action: string, params: any = {}) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/jotform-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({ action, params })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Error desconocido');
    }

    return result.data;
  };

  // Obtener lista de formularios (Híbrido: DB filtrada por proyecto + datos JotForm)
  const { data: forms, isLoading: isLoadingForms } = useQuery({
    queryKey: ['jotform-forms', projectId],
    queryFn: async () => {
      console.log('🔍 useJotFormData: Obteniendo formularios para proyecto', projectId);

      try {
        // 1. Obtener mappings del proyecto desde DB (filtrado por proyecto)
        const { data: projectMappings, error: dbError } = await supabase
          .from('jotform_form_mappings')
          .select('jotform_form_id, form_title')
          .eq('project_id', projectId)
          .not('jotform_form_id', 'is', null)
          .neq('jotform_form_id', '');

        if (dbError) {
          console.error('❌ Error obteniendo mappings de DB:', dbError);
          throw dbError;
        }

        console.log(`✅ Mappings del proyecto ${projectId}:`, projectMappings);

        // Si no hay mappings, devolver array vacío
        if (!projectMappings || projectMappings.length === 0) {
          console.log('⚠️ No hay formularios mapeados para este proyecto');
          return { forms: [] };
        }

        // 2. Obtener datos actualizados de JotForm API
        let jotformData: any;
        try {
          jotformData = await callJotFormAPI('list-forms', {
            offset: 0,
            limit: 50,
            orderby: 'created_at',
            status: 'ENABLED',
            search: ''
          });
          console.log('✅ Datos de JotForm API obtenidos:', jotformData);
        } catch (apiError) {
          console.warn('⚠️ JotForm API no disponible, usando solo datos de DB:', apiError);
          // Fallback: usar solo datos de DB sin status/count
          return {
            forms: projectMappings.map(mapping => ({
              id: mapping.jotform_form_id,
              title: mapping.form_title || 'Sin título',
              status: 'UNKNOWN',
              submission_count: 0
            }))
          };
        }

        // 3. JOIN: combinar datos de DB (filtrados por proyecto) con datos de JotForm (actualizados)
        const projectForms = projectMappings
          .map(mapping => {
            const jotformForm = jotformData?.forms?.find((f: any) => f.id === mapping.jotform_form_id);

            return {
              id: mapping.jotform_form_id,
              title: mapping.form_title || jotformForm?.title || 'Sin título',
              status: jotformForm?.status || 'UNKNOWN',
              submission_count: jotformForm?.count || 0
            };
          })
          .filter(form => form.id); // Excluir cualquier mapping sin jotform_form_id

        console.log(`✅ Formularios del proyecto ${projectId} (híbrido):`, projectForms);

        return { forms: projectForms };
      } catch (error) {
        console.error('❌ Error en query híbrida:', error);
        // Fallback completo para testing
        return {
          forms: [
            { id: '252472726881061', title: 'Queremos escucharle: Servicio Jurídico', status: 'ENABLED', submission_count: 17 },
            { id: '252461493351355', title: 'Formulario Referidos', status: 'ENABLED', submission_count: 71 },
            { id: '251246163578360', title: 'Evaluación Líder 3M v1', status: 'ENABLED', submission_count: 195 }
          ]
        };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    enabled: !!projectId, // Solo ejecutar si tenemos projectId
  });

  // Obtener submissions del formulario seleccionado
  const { data: submissions, isLoading: isLoadingSubmissions, error } = useQuery({
    queryKey: ['jotform-submissions', selectedFormId, limit, offset],
    queryFn: async () => {
      if (!selectedFormId) return { submissions: [] };

      try {
        return await callJotFormAPI('get-submissions', {
          formId: selectedFormId,
          limit,
          offset,
          orderby: 'created_at',
          direction: 'DESC'
        });
      } catch (error) {
        console.error('Error al obtener submissions:', error);
        // Fallback para testing con datos del formulario de Servicio Jurídico
        if (selectedFormId === '252472726881061') {
          return {
            submissions: generateMockJuridicoData()
          };
        }
        return { submissions: [] };
      }
    },
    enabled: !!selectedFormId,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });

  // Función para analizar los datos de las submissions
  // Función para procesar datos de matriz de valoración por áreas
  const processAreaRatings = (submissions: JotFormSubmission[]) => {
    console.log('\n📊 INICIANDO PROCESAMIENTO DE ÁREAS - SOLO DATOS REALES');
    
    // Si no hay submissions, devolver estructura vacía
    if (!submissions || submissions.length === 0) {
      console.log('📊 Sin submissions - devolviendo estructura vacía');
      return {
        areaAverages: {},
        areaDistributions: {}
      };
    }
    
    // Mapeo de filas de la matriz a áreas según la estructura del formulario JotForm
    const rowToAreaMap: Record<number, string> = {
      0: 'Área Fiscal',
      1: 'Área Colegios', 
      2: 'Área Universidades',
      3: 'Área Centros y Obras apostólicas',
      4: 'Área Contenciosos',
      5: 'Área Archivo',
      6: 'Dirección Jurídico'
    };

    const areaRatings: Record<string, number[]> = {};
    const areaDistributions: Record<string, Array<{ rating: number; count: number }>> = {};
    
    // Inicializar arrays para cada área
    Object.values(rowToAreaMap).forEach(area => {
      areaRatings[area] = [];
    });

    console.log(`📍 Procesando ${submissions.length} submissions para áreas`);

    let hasAreaData = false;

    submissions.forEach((submission, submissionIndex) => {
      Object.entries(submission.answers || {}).forEach(([questionId, answer]: [string, any]) => {
        const questionText = (answer.text || answer.name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
        
        // Buscar preguntas relacionadas con áreas/valoración
        const isAreaQuestion = questionText.toLowerCase().includes('área') || 
                              questionText.toLowerCase().includes('servicio') ||
                              questionText.toLowerCase().includes('valora') ||
                              questionText.toLowerCase().includes('califica');
        
        if (isAreaQuestion && answer.answer) {
          console.log(`\n📍 SUBMISSION ${submissionIndex + 1} - PREGUNTA ÁREA: "${questionText}"`);
          console.log(`📍 Tipo de respuesta:`, typeof answer.answer);
          console.log(`📍 Respuesta completa:`, answer.answer);
          
          // CASO 1: Respuesta es array de arrays (matriz de JotForm)
          if (Array.isArray(answer.answer)) {
            console.log(`📍 ✅ MATRIZ DETECTADA - Procesando ${answer.answer.length} filas`);
            
            answer.answer.forEach((row: any[], rowIndex: number) => {
              const areaName = rowToAreaMap[rowIndex];
              
              if (areaName && Array.isArray(row)) {
                console.log(`📍   Fila ${rowIndex} (${areaName}): [${row.join(', ')}]`);
                
                // Encontrar la posición no vacía en la fila
                // En JotForm, solo una posición está marcada por fila
                row.forEach((value, colIndex) => {
                  if (value && value !== '' && value !== null && value !== undefined) {
                    // La posición + 1 es el rating (arrays 0-indexed, ratings 1-10)
                    const rating = colIndex + 1;
                    if (rating >= 1 && rating <= 10) {
                      areaRatings[areaName].push(rating);
                      hasAreaData = true;
                      console.log(`📍   ✅ ${areaName}: Rating ${rating} (columna ${colIndex})`);
                    }
                  }
                });
              }
            });
          }
          
          // CASO 2: Respuesta es string con valores separados
          else if (typeof answer.answer === 'string') {
            console.log(`📍 STRING DETECTADO - Intentando parsear`);
            
            // Si contiene comas, puede ser matriz serializada
            if (answer.answer.includes(',')) {
              const values = answer.answer.split(',').map(v => v.trim());
              console.log(`📍 Valores separados por coma:`, values);
              
              // Mapear cada valor a su área correspondiente
              values.forEach((value, index) => {
                const areaName = rowToAreaMap[index];
                if (areaName && value) {
                  const numericMatch = value.match(/(\d+)/);
                  if (numericMatch) {
                    const rating = parseInt(numericMatch[1]);
                    if (rating >= 1 && rating <= 10) {
                      areaRatings[areaName].push(rating);
                      hasAreaData = true;
                      console.log(`📍   ✅ ${areaName}: Rating ${rating} (índice ${index})`);
                    }
                  }
                }
              });
            }
          }
        }
      });
    });

    // IMPORTANTE: Solo devolver datos reales, no fallback a mock
    if (!hasAreaData) {
      console.log('📊 ❌ No se encontraron datos de área reales');
      return {
        areaAverages: {},
        areaDistributions: {}
      };
    }

    // Calcular promedios y distribuciones SOLO con datos reales
    const areaAverages: Record<string, number> = {};
    Object.entries(areaRatings).forEach(([area, ratings]) => {
      console.log(`\n📊 PROCESANDO ${area}:`);
      console.log(`📊   Ratings encontrados: [${ratings.join(', ')}]`);
      
      if (ratings.length > 0) {
        const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
        areaAverages[area] = Math.round(average * 10) / 10;
        
        // Crear distribución
        const distribution: { [key: number]: number } = {};
        ratings.forEach(rating => {
          distribution[rating] = (distribution[rating] || 0) + 1;
        });
        
        areaDistributions[area] = Object.entries(distribution)
          .map(([rating, count]) => ({
            rating: parseInt(rating),
            count: count
          }))
          .sort((a, b) => a.rating - b.rating);
        
        console.log(`📊   Promedio: ${areaAverages[area]}`);
        console.log(`📊   Distribución:`, areaDistributions[area]);
      } else {
        console.log(`📊   Sin datos`);
      }
    });

    console.log('\n🎯 RESULTADO FINAL DE ÁREAS (SOLO DATOS REALES):');
    console.log('🎯 Promedios por área:', areaAverages);
    console.log('🎯 Áreas con datos:', Object.keys(areaAverages).length);
    
    const result = { areaAverages, areaDistributions };
    console.log('🎯 Objeto completo que se devuelve:', result);
    return result;
  };

  const analyzeSubmissions = (submissions: JotFormSubmission[]) => {
    if (!submissions || submissions.length === 0) {
      return {
        totalResponses: 0,
        demographics: {},
        ratings: {},
        textResponses: {},
        choices: {},
        areaAverages: {},
        areaDistributions: {}
      };
    }

    const analysis: {
      totalResponses: number;
      demographics: Record<string, Record<string, number>>;
      ratings: Record<string, Array<{ rating: number; count: number }>>;
      textResponses: Record<string, string[]>;
      choices: Record<string, Record<string, number>>;
      areaAverages: Record<string, number>;
      areaDistributions: Record<string, Array<{ rating: number; count: number }>>;
    } = {
      totalResponses: submissions.length,
      demographics: {},
      ratings: {},
      textResponses: {},
      choices: {},
      ...processAreaRatings(submissions)
    };

    // Analizar cada submission
    submissions.forEach((submission) => {
      Object.entries(submission.answers || {}).forEach(([questionId, answer]: [string, any]) => {
        
        let questionText = answer.text || answer.name || `Pregunta ${questionId}`;
        // Limpiar texto entre paréntesis al final de la pregunta
        questionText = questionText.replace(/\s*\([^)]*\)\s*$/, '').trim();
        let answerText = answer.answer || answer.text || '';

        console.log(`🔍 Procesando pregunta: "${questionText}" | Respuesta: "${answerText}"`);

        // Manejar casos donde answerText es un objeto
        if (typeof answerText === 'object' && answerText !== null) {
          if (answerText.first && answerText.last) {
            answerText = `${answerText.first} ${answerText.last}`;
          } else if (answerText.value) {
            answerText = answerText.value;
          } else {
            answerText = JSON.stringify(answerText);
          }
        }

        if (!answerText || answerText === '{}') return;

        // Detectar si es una calificación numérica simple (1-10)
        const numericValue = parseFloat(answerText);
        
        if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 10 && Number.isInteger(numericValue)) {
          if (!analysis.ratings[questionText]) {
            analysis.ratings[questionText] = [];
          }

          const existingRating = analysis.ratings[questionText].find(r => r.rating === numericValue);
          if (existingRating) {
            existingRating.count++;
          } else {
            analysis.ratings[questionText].push({ rating: numericValue, count: 1 });
          }
        }
        // Detectar respuestas de opción múltiple/selección
        else if (typeof answerText === 'string' && answerText.length < 100) {
          // Considerar como opción si es texto corto
          if (!analysis.choices[questionText]) {
            analysis.choices[questionText] = {};
          }

          analysis.choices[questionText][answerText] = (analysis.choices[questionText][answerText] || 0) + 1;

          // También agregar a demografía si parece ser datos demográficos
          const demographicKeywords = ['edad', 'sexo', 'genero', 'área', 'area', 'rol', 'puesto', 'ciudad', 'tiempo'];
          if (demographicKeywords.some(keyword => questionText.toLowerCase().includes(keyword))) {
            if (!analysis.demographics[questionText]) {
              analysis.demographics[questionText] = {};
            }
            analysis.demographics[questionText][answerText] = (analysis.demographics[questionText][answerText] || 0) + 1;
          }
        }
        // Respuestas de texto libre
        else {
          if (!analysis.textResponses[questionText]) {
            analysis.textResponses[questionText] = [];
          }

          // Manejar objetos complejos (como nombres que tienen {first, last})
          let textToAdd = answerText;
          if (typeof answerText === 'object' && answerText !== null) {
            if (answerText.first && answerText.last) {
              textToAdd = `${answerText.first} ${answerText.last}`;
            } else if (answerText.value) {
              textToAdd = answerText.value;
            } else {
              textToAdd = JSON.stringify(answerText);
            }
          }

          analysis.textResponses[questionText].push(textToAdd);
        }
      });
    });

    // Ordenar ratings por rating
    Object.keys(analysis.ratings).forEach(question => {
      analysis.ratings[question].sort((a, b) => a.rating - b.rating);
    });

    return analysis;
  };

  const selectedForm = forms?.forms?.find((form: any) => form.id === selectedFormId);
  const analysisData = submissions?.submissions ? analyzeSubmissions(submissions.submissions) : null;

  return {
    // Forms data
    forms: forms?.forms || [],
    isLoadingForms,

    // Selected form
    selectedFormId,
    setSelectedFormId,
    selectedForm,

    // Submissions data
    submissions: submissions?.submissions || [],
    isLoadingSubmissions,
    error,

    // Analysis
    analysisData,

    // Helper methods
    calculateAverage: (ratings: Array<{ rating: number; count: number }>) => {
      if (!ratings || ratings.length === 0) return 0;
      const total = ratings.reduce((sum, item) => sum + (item.rating * item.count), 0);
      const totalCount = ratings.reduce((sum, item) => sum + item.count, 0);
      return totalCount > 0 ? (total / totalCount) : 0;
    }
  };
};